'use client'

import { X } from 'lucide-react'
import type { useRouter } from 'next/navigation'
import { useEffect, useRef } from 'react'

import { HOVER_READ_DELAY_MS, iconMap, typeStyles } from './notificationBell.constants'
import { Button } from '@fp/ui-kit'
import type { Notification } from '@hooks/useNotifications'
import { resolveAction, getActionIcon } from '@lib/actionRegistry'
import { cn } from '@lib/cn'

interface NotificationItemProps {
  notification: Notification
  onDismiss: (id: string) => void
  onToggleRead: (notification: Notification) => void
  onMarkRead: (id: string) => void
  actionCtx: { router: ReturnType<typeof useRouter>; pathname: string }
}

export const NotificationItem = ({
  notification,
  onDismiss,
  onToggleRead,
  onMarkRead,
  actionCtx,
}: NotificationItemProps) => {
  const Icon = iconMap[notification.type]
  const isRead = !!notification.readAt
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const handleMouseEnter = () => {
    if (isRead) return
    hoverTimerRef.current = setTimeout(() => {
      onMarkRead(notification.id)
      hoverTimerRef.current = null
    }, HOVER_READ_DELAY_MS)
  }

  const handleMouseLeave = () => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current)
      hoverTimerRef.current = null
    }
  }

  useEffect(() => {
    return () => {
      if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current)
    }
  }, [])

  const styles = typeStyles[notification.type]

  return (
    <div
      className={cn(
        'p-3 border-b border-border last:border-0 cursor-pointer transition-all',
        isRead ? 'opacity-70' : styles.tint,
      )}
      onClick={() => onToggleRead(notification)}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      role='button'
      tabIndex={0}
    >
      <div className='flex items-start gap-2'>
        <Icon className={cn('h-4 w-4 mt-0.5 shrink-0', styles.text)} />
        <div className='flex-1 min-w-0'>
          <div className='flex items-center gap-1.5'>
            <span
              className={cn(
                'h-1.5 w-1.5 shrink-0 rounded-full',
                isRead ? 'bg-muted-foreground/50' : [styles.bg, 'animate-pulse'],
              )}
            />
            <p className='text-sm font-medium'>{notification.title}</p>
          </div>
          {notification.message && <p className='text-xs text-muted-foreground mt-0.5'>{notification.message}</p>}

          {notification.actions && notification.actions.length > 0 && (
            <div className='flex gap-2 mt-2'>
              {notification.actions.map((action, idx) => {
                const handler = resolveAction(action.actionType, actionCtx, action.payload)
                if (!handler) return null

                const ActionIcon = action.icon ? getActionIcon(action.actionType) : undefined

                return (
                  <Button
                    key={idx}
                    variant={idx === 0 ? 'default' : 'outline'}
                    size='sm'
                    className={cn('h-7 text-xs px-2', `${styles.bg}/5`, styles.text, styles.hoverBg)}
                    onClick={(e) => {
                      e.stopPropagation()
                      handler()
                    }}
                  >
                    {ActionIcon && <ActionIcon className='h-3 w-3' />}
                    {action.label}
                  </Button>
                )
              })}
            </div>
          )}
        </div>

        <Button
          variant='ghost'
          size='sm'
          className='h-6 w-6 p-0 shrink-0'
          onClick={(e) => {
            e.stopPropagation()
            onDismiss(notification.id)
          }}
        >
          <X className='h-3 w-3' />
        </Button>
      </div>
    </div>
  )
}
